import { useEffect, useMemo, useState } from 'react';

import { EmptyState, Spinner } from '../../components';
import { ChevronLeftIcon, ClipboardCheckIcon } from '../../layout/icons';
import { useQltSlotAttendanceStore } from '../../stores/qlt';
import { useSubmissionStore } from './store';
import { formatAverage, taskGradingStats, type GradingStats } from './gradingStats';
import { groupTasks, rosterOf, shownRoster, statusLabel, submittedInOrder, vnDate, vnDateTime, type RosterStudent, type StudentSubmission, type SubmissionTask } from './submissions';
import '../../styles/submissions.css';

export interface SubmissionsPanelProps {
  classTitle: string;
  onOpen: (taskTitle: string, order: StudentSubmission[], index: number) => void;
}

function statusClass(submission: StudentSubmission | null): string {
  if (!submission) return 'hw-status hw-status--missing';
  return submission.approved ? 'hw-status hw-status--marked' : 'hw-status hw-status--submitted';
}

// anonymous by construction: counts, an average and bands, never a name, so it shows while projected
function GradingSummary({ stats }: { stats: GradingStats }) {
  const bands = stats.bands;
  const max = bands ? Math.max.apply(null, bands.map((band) => band.count)) : 0;
  return (
    <div className="hw-stats">
      <p className="hw-stats__line">
        <strong>{stats.graded}/{stats.submitted}</strong> bài đã chấm
        {stats.average !== null ? <> • Điểm TB <strong>{formatAverage(stats.average)}</strong></> : null}
      </p>
      {bands ? (
        <div className="hw-stats__bars" role="img" aria-label={`Phân bố điểm: ${bands.map((band) => `${band.label}: ${band.count}`).join(', ')}`}>
          {bands.map((band) => (
            <div key={band.label} className="hw-stats__band">
              <span className="hw-stats__count">{band.count}</span>
              <span className="hw-stats__track"><span className="hw-stats__fill" style={{ height: `${max > 0 ? Math.round((band.count / max) * 100) : 0}%`, background: band.color }} /></span>
              <span className="hw-stats__label">{band.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TaskDetail({ task, roster, courseTitle, onBack, onOpen }: { task: SubmissionTask; roster: RosterStudent[]; courseTitle: string; onBack: () => void; onOpen: SubmissionsPanelProps['onOpen'] }) {
  const showMarks = useSubmissionStore((state) => state.showMarks);
  const setShowMarks = useSubmissionStore((state) => state.setShowMarks);
  const entries = shownRoster(task, roster, showMarks);
  const order = submittedInOrder(task, roster);
  return (
    <div className="hw-panel">
      <div className="hw-panel__crumb">
        <button type="button" className="libpanel__back" aria-label="Quay lại danh sách bài tập" onClick={onBack}><ChevronLeftIcon size={18} /></button>
        <div className="hw-panel__crumb-text">
          <h3 title={task.title}>{task.title}</h3>
          <p>{[`${task.count}/${roster.length} học sinh đã nộp`, courseTitle].filter(Boolean).join(' • ')}</p>
        </div>
        <button type="button" className="hw-panel__refresh" aria-pressed={showMarks} onClick={() => setShowMarks(!showMarks)}>{showMarks ? 'Ẩn điểm' : 'Hiện điểm'}</button>
      </div>
      <GradingSummary stats={taskGradingStats(task)} />
      <button type="button" className="primary-button hw-panel__start" disabled={order.length === 0} onClick={() => onOpen(task.title, order, 0)}>Mở lần lượt trên bảng</button>
      <ul className="hw-roster">
        {entries.map((entry) => {
          const submission = entry.submission;
          const at = submission ? order.indexOf(submission) : -1;
          return (
            <li key={entry.student.code}>
              <button type="button" className="hw-roster__row" disabled={!submission} onClick={() => onOpen(task.title, order, at)}>
                <span className="hw-roster__name">{entry.student.name}</span>
                <span className="hw-roster__meta">
                  {showMarks ? <span className={statusClass(submission)}>{statusLabel(submission)}</span> : null}
                  {submission && submission.submittedAt ? <span className="hw-roster__time">{vnDateTime(submission.submittedAt)}</span> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Bài nộp: what this period's class handed in on IZTeach, so the teacher shows it instead of checking notebooks
export function SubmissionsPanel({ classTitle, onOpen }: SubmissionsPanelProps) {
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const loading = useSubmissionStore((state) => state.loading);
  const error = useSubmissionStore((state) => state.error);
  const submissions = useSubmissionStore((state) => state.submissions);
  const courseTitles = useSubmissionStore((state) => state.courseTitles);
  const load = useSubmissionStore((state) => state.load);
  const [taskId, setTaskId] = useState<string | null>(null);
  // closing the panel ends the session unless it closed to show a submission on the board
  useEffect(() => () => { const state = useSubmissionStore.getState(); if (!state.viewer) state.setShowMarks(false); }, []);

  const roster = useMemo(() => rosterOf(rows), [rows]);
  const codes = useMemo(() => roster.map((student) => student.code), [roster]);
  const key = codes.join(',');
  useEffect(() => { if (codes.length > 0) void load(key, codes); }, [key]);

  const tasks = useMemo(() => groupTasks(submissions, roster), [submissions, roster]);
  const task = taskId ? tasks.filter((item) => item.id === taskId)[0] || null : null;

  if (roster.length === 0) {
    if (rosterLoading) return <Spinner label="Đang tải danh sách lớp…" />;
    return <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Chưa có danh sách lớp" description="Bảng chưa tải được danh sách học sinh của tiết này nên chưa tìm được bài nộp." />;
  }
  if (task) return <TaskDetail task={task} roster={roster} courseTitle={courseTitles[task.courseId] || ''} onBack={() => setTaskId(null)} onOpen={onOpen} />;
  if (loading && tasks.length === 0) return <Spinner label="Đang tìm bài nộp của lớp…" />;
  if (error) return <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Chưa tải được bài nộp" description={error} action={<button type="button" className="secondary-button" onClick={() => void load(key, codes, true)}>Thử lại</button>} />;
  if (tasks.length === 0) return <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Chưa có bài nộp nào" description={`Học sinh lớp ${classTitle || 'này'} chưa nộp bài tập về nhà nào trên IZTeach.`} action={<button type="button" className="secondary-button" onClick={() => void load(key, codes, true)}>Tải lại</button>} />;

  const years: string[] = [];
  tasks.forEach((item) => { if (years.indexOf(item.schoolYear) < 0) years.push(item.schoolYear); });
  return (
    <div className="hw-panel">
      <div className="hw-panel__bar">
        <p>Bài tập học sinh lớp {classTitle || 'này'} đã nộp trên IZTeach.</p>
        <button type="button" className="hw-panel__refresh" disabled={loading} onClick={() => void load(key, codes, true)}>{loading ? 'Đang tải…' : 'Tải lại'}</button>
      </div>
      {years.map((year) => (
        <section key={year || 'none'} className="hw-panel__year">
          <h3>{year ? `Năm học ${year}` : 'Chưa rõ ngày nộp'}</h3>
          <ul className="up-list">
            {tasks.filter((item) => item.schoolYear === year).map((item) => (
              <li key={item.id}>
                <button type="button" className="up-list__row" onClick={() => setTaskId(item.id)}>
                  <span className="up-list__mark" aria-hidden={true}><ClipboardCheckIcon size={18} /></span>
                  <span className="up-list__text">
                    <span className="up-list__name" title={item.title}>{item.title}</span>
                    <span className="up-list__meta">{[`${item.count}/${roster.length} đã nộp`, item.latestAt ? `mới nhất ${vnDate(item.latestAt)}` : ''].filter(Boolean).join(' • ')}</span>
                    {courseTitles[item.courseId] ? <span className="up-list__meta" title={courseTitles[item.courseId]}>{courseTitles[item.courseId]}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
